/**
 * PWA Install Prompt Component
 * Shows a popup to install the app on mobile/tablet devices
 */
import { useState, useEffect } from "react";
import { X, Download, Smartphone } from "lucide-react";

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if on iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Check if mobile or tablet
    const isMobileOrTablet = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);

    if (!isMobileOrTablet) {
      return; // Don't show on desktop
    }

    // Check if user has dismissed the prompt recently (within 7 days)
    const dismissedAt = localStorage.getItem('pwaPromptDismissed');
    if (dismissedAt) {
      const dismissedDate = new Date(parseInt(dismissedAt));
      const daysSinceDismissed = (Date.now() - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // For iOS, show prompt after a delay
    if (isIOSDevice) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // For Android/other, listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Check if app was installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      // iOS doesn't support programmatic install, just show instructions
      return;
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwaPromptDismissed', Date.now().toString());
  };

  if (!showPrompt || isInstalled) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[9999] flex items-end sm:items-center justify-center p-4 animate-fadeIn">
      <div 
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl transform transition-all animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 pb-4">
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition"
            aria-label="Chiudi"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl flex items-center justify-center shadow-lg">
              <img 
                src="/logo192.svg" 
                alt="LoJack" 
                className="w-10 h-10"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center justify-center">
                <Smartphone className="text-white" size={28} />
              </div>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">LoJack Italia</h2>
              <p className="text-sm text-gray-500">Calendario Installatori</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <p className="text-gray-600 text-sm leading-relaxed">
            Installa l&apos;app sul tuo dispositivo per un accesso rapido e 
            un&apos;esperienza ottimizzata anche offline.
          </p>
          
          {/* Benefits */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check size={14} className="text-green-600" />
              </div>
              <span>Accesso rapido dalla home</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check size={14} className="text-green-600" />
              </div>
              <span>Funziona anche offline</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-700">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <Check size={14} className="text-green-600" />
              </div>
              <span>Notifiche push</span>
            </div>
          </div>
        </div>

        {/* iOS Instructions */}
        {isIOS && (
          <div className="px-6 pb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-sm font-medium text-blue-800 mb-2">
                Come installare su iPhone/iPad:
              </p>
              <ol className="text-sm text-blue-700 space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="font-bold">1.</span>
                  <span>Tocca l&apos;icona <strong>Condividi</strong> (quadrato con freccia) in basso</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">2.</span>
                  <span>Scorri e tocca <strong>&quot;Aggiungi a Home&quot;</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="font-bold">3.</span>
                  <span>Conferma toccando <strong>&quot;Aggiungi&quot;</strong></span>
                </li>
              </ol>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="p-6 pt-2 space-y-3">
          {!isIOS && (
            <button
              onClick={handleInstall}
              className="w-full py-3.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <Download size={20} />
              Installa App
            </button>
          )}
          
          <button
            onClick={handleDismiss}
            className="w-full py-3 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition"
          >
            {isIOS ? 'Ho capito' : 'Non ora'}
          </button>
        </div>

        {/* Bottom indicator for mobile */}
        <div className="flex justify-center pb-4 sm:hidden">
          <div className="w-12 h-1 bg-gray-300 rounded-full"></div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

// Import Check icon
const Check = ({ size, className }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="20 6 9 17 4 12"></polyline>
  </svg>
);

export default PWAInstallPrompt;
